import React, { Component } from "react";
import { connect } from "react-redux";

import FullScreenLayout from "../../components/FullScreenLayout";
import SiteAreaEditor from "./SiteAreaEditor";
import NavBar from "../../NavBar/NavBar";
import Alert from "../../components/Alert";
import Throbber from "../../components/Throbber";

import { CurrentSiteState } from "../../../redux/modules/currentSite/reducer";
import { RequestGcpSuggestion } from "./types";
import { Err } from "../../../typings";
import { AppDispatch } from "../../../store";
import { requestGcpSuggestion } from "../../../apiFacades/bws/requestGcpSuggestion";
import {
  fetchSite,
  ISRS,
  SiteBoundary,
  updateSiteBoundary,
} from "../../../apiFacades/csu/site";
import { regularStyle, TextItem } from "@traceair/tds";

type SiteAreaEditPageProps = {
  // passed from Router via props
  customerId: string;
  siteId: string;

  // passed from store via connect
  currentSiteState: CurrentSiteState;
  dispatch: AppDispatch;
};

type SiteAreaEditPageState = {
  requestGcpSuggestion: RequestGcpSuggestion;
  isLoading: boolean;
  loadingErrorMessage: string | null;
  siteBoundary: SiteBoundary | null;
  siteSrs: ISRS | null;
};

class SiteAreaEditPage extends Component<
  SiteAreaEditPageProps,
  SiteAreaEditPageState
> {
  constructor(props: SiteAreaEditPageProps) {
    super(props);
    this.state = {
      requestGcpSuggestion: {
        show: false,
        status: "NOT_STARTED",
      },
      isLoading: true,
      loadingErrorMessage: null,
      siteBoundary: null,
      siteSrs: null,
    };
  }

  async componentDidMount() {
    const { dispatch, customerId, siteId } = this.props;

    try {
      const site = await dispatch(fetchSite(customerId, siteId));
      this.setState({
        isLoading: false,
        siteBoundary: site.boundary,
        siteSrs: site.srs,
      });
    } catch (e) {
      this.setState({
        loadingErrorMessage: e.message,
      });
    }
  }

  handleBoundaryChange = (newBoundary: SiteBoundary) => {
    const { customerId, siteId, dispatch } = this.props;

    this.setState({
      siteBoundary: newBoundary,
    });

    dispatch(updateSiteBoundary(customerId, siteId, newBoundary))
      .then(() => {
        this.setState({
          requestGcpSuggestion: { show: true, status: "NOT_STARTED" },
        });
      })
      .catch((e: Error) => {
        alert(e.message);
      });
  };

  handleUpdateRequestGcpSuggestion = (requestIsNeeded: boolean) => {
    const { customerId, siteId, dispatch } = this.props;
    const { status } = this.state.requestGcpSuggestion;
    const updatedRequestGcpSuggestion = {
      show: true,
      status,
    };

    if (requestIsNeeded) {
      updatedRequestGcpSuggestion.show = false;
      this.setState({ requestGcpSuggestion: updatedRequestGcpSuggestion });
      dispatch(requestGcpSuggestion(customerId, siteId))
        .then(() => {
          updatedRequestGcpSuggestion.status = "DONE";
          this.setState({ requestGcpSuggestion: updatedRequestGcpSuggestion });
        })
        .catch((err: Err) => {
          updatedRequestGcpSuggestion.status = "FAILED";
          this.setState({ requestGcpSuggestion: updatedRequestGcpSuggestion });
          console.error(err);
        });
    } else {
      updatedRequestGcpSuggestion.show = false;
      this.setState({ requestGcpSuggestion: updatedRequestGcpSuggestion });
    }
  };

  render() {
    return (
      <FullScreenLayout navBar={<NavBar />} content={this.renderContent()} />
    );
  }

  renderContent() {
    const { customerId, siteId } = this.props;
    const {
      requestGcpSuggestion,
      loadingErrorMessage,
      isLoading,
      siteBoundary,
      siteSrs,
    } = this.state;

    if (loadingErrorMessage) {
      return <Alert>{loadingErrorMessage}</Alert>;
    }

    if (isLoading) {
      return <Throbber />;
    }

    if (!siteBoundary) {
      return (
        <Alert type="failure">
          <TextItem style={regularStyle} text="Failed to load site boundary" />
        </Alert>
      );
    }

    return (
      <SiteAreaEditor
        value={siteBoundary}
        onChange={this.handleBoundaryChange}
        customerId={customerId}
        siteId={siteId}
        srs={siteSrs}
        requestGcpSuggestion={requestGcpSuggestion}
        onUpdateRequestGcpSuggestion={this.handleUpdateRequestGcpSuggestion}
      />
    );
  }
}

export default connect()(SiteAreaEditPage);
